/**
 * Cash Flow Inputs hub — Access `frmReportCashFlowMain`.
 *
 * A "Go to Page" combo (cbPreferencesList) swaps the embedded list across 9
 * pages; the toolbar reconfigures per page (cbPreferencesList_Change). Buttons
 * open the New/Edit/Need/Misa dialogs (DoCmd.OpenForm) or soft-delete a mapping.
 * Permission: SCREEN_ACCESS gates the action buttons (disabled when !canEdit).
 */
import { useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ListAltIcon from '@mui/icons-material/ListAlt'
import PostAddIcon from '@mui/icons-material/PostAdd'
import { Autocomplete, Box, Button, Stack, TextField, type SxProps, type Theme } from '@mui/material'
import { usePagePermission } from '../../../hooks/usePagePermission'
import { HIDE_ON_MOBILE_INLINE } from '../../../theme/responsive'
import ConfirmDialog from '../../../components/ConfirmDialog'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../../futures/FuturesColumnsMenu'
import {
  useGetCashflowInputQuery,
  useGetCashflowBankBalanceQuery,
  useGetCashflowMapCpQuery,
  useGetCashflowMapServiceQuery,
  useGetCashflowMapMuaHangQuery,
  useGetCashflowMapBanHangQuery,
  useGetCashflowWeeksQuery,
  useDeleteCashflowMapMuaHangMutation,
  useDeleteCashflowMapBanHangMutation,
  type MapMuaHang,
  type MapBanHang,
} from '../../../store/api/reportCashflowApi'
import CashflowDataList, { type CashflowColumn } from './CashflowDataList'
import {
  CASHFLOW_PAGES,
  BANK_COLUMNS,
  CP_COLUMNS,
  SERVICE_COLUMNS,
  MUAHANG_COLUMNS,
  BANHANG_COLUMNS,
  type CashflowAction,
  type CashflowPageCfg,
  type CashflowPageKey,
  type ToolbarButtonCfg,
} from './cashflowPages'
import CashflowViewerDialog from './CashflowViewerDialog'
import InputValueDialog from './dialogs/InputValueDialog'
import CpMappingEditDialog from './dialogs/CpMappingEditDialog'
import ServiceMappingEditDialog from './dialogs/ServiceMappingEditDialog'
import MuaHangMapDialog from './dialogs/MuaHangMapDialog'
import BanHangMapDialog from './dialogs/BanHangMapDialog'

const toolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const ICONS = {
  new: <AddIcon sx={{ fontSize: 16 }} />,
  edit: <EditOutlinedIcon sx={{ fontSize: 16 }} />,
  delete: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
  list: <ListAltIcon sx={{ fontSize: 16 }} />,
  value: <PostAddIcon sx={{ fontSize: 16 }} />,
}

const isInputPage = (k: CashflowPageKey) =>
  k === 'load_on_board' || k === 'project_new_p' || k === 'project_new_npe' || k === 'others'

export default function CashFlowInputsView() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const [pageKey, setPageKey] = useState<CashflowPageKey>('load_on_board')
  const [selectedId, setSelectedId] = useState<string | number | null>(null)
  const page = useMemo<CashflowPageCfg>(() => CASHFLOW_PAGES.find((p) => p.key === pageKey)!, [pageKey])

  // Dialog state
  const [inputOpen, setInputOpen] = useState(false)
  const [cpEditOpen, setCpEditOpen] = useState(false)
  const [svcEditOpen, setSvcEditOpen] = useState(false)
  const [mhDialog, setMhDialog] = useState<{ mode: 'new' | 'edit' } | null>(null)
  const [bhDialog, setBhDialog] = useState<{ mode: 'new' | 'edit' } | null>(null)
  const [viewer, setViewer] = useState<CashflowAction | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ kind: 'mh' | 'bh'; id: number } | null>(null)

  const [deleteMuaHang] = useDeleteCashflowMapMuaHangMutation()
  const [deleteBanHang] = useDeleteCashflowMapBanHangMutation()

  // Week dimension for the Input matrix headers.
  const { data: weeks } = useGetCashflowWeeksQuery()

  // ── Query hooks (skip the inactive ones). ──────────────────────────────
  const inputQ = useGetCashflowInputQuery({ page: pageKey }, { skip: !isInputPage(pageKey), refetchOnMountOrArgChange: true })
  const bankQ = useGetCashflowBankBalanceQuery(undefined, { skip: pageKey !== 'bank' })
  const cpQ = useGetCashflowMapCpQuery(undefined, { skip: pageKey !== 'cp' })
  const svcQ = useGetCashflowMapServiceQuery(undefined, { skip: pageKey !== 'service' })
  const mhQ = useGetCashflowMapMuaHangQuery(undefined, { skip: pageKey !== 'muahang' })
  const bhQ = useGetCashflowMapBanHangQuery(undefined, { skip: pageKey !== 'banhang' })

  // ── Active list config. ────────────────────────────────────────────────
  const inputColumns = useMemo<CashflowColumn<Record<string, unknown>>[]>(() => {
    const weekCols: CashflowColumn<Record<string, unknown>>[] = (weeks ?? []).map((w) => ({
      key: w.weekCode,
      label: w.weekName,
      kind: 'number' as const,
      align: 'right' as const,
      width: 92,
    }))
    if (pageKey === 'others') {
      return [{ key: 'inputName', label: 'InputType' }, ...weekCols]
    }
    return [
      { key: 'qualityCode', label: 'QualityCode' },
      { key: 'category', label: 'Category' },
      ...weekCols,
      { key: 'priceFromG2', label: 'Price vs G2', kind: 'number', align: 'right' },
      { key: 'description', label: 'Description', wrap: true, width: 240 },
      { key: 'inputName', label: 'InputType' },
    ]
  }, [weeks, pageKey])

  const active = useMemo(() => {
    switch (pageKey) {
      case 'bank':
        return {
          columns: BANK_COLUMNS,
          q: bankQ,
          getRowId: (r: Record<string, unknown>) => String(r.bankAccountNumber ?? ''),
          selectable: false,
          search: 'Bank number, name…',
        }
      case 'cp':
        return {
          columns: CP_COLUMNS,
          q: cpQ,
          getRowId: (r: Record<string, unknown>) => String(r.erpCode ?? ''),
          selectable: true,
          search: 'ERP/MISA code, name…',
        }
      case 'service':
        return {
          columns: SERVICE_COLUMNS,
          q: svcQ,
          getRowId: (r: Record<string, unknown>) => String(r.serviceCode ?? ''),
          selectable: true,
          search: 'Service code, name…',
        }
      case 'muahang':
        return {
          columns: MUAHANG_COLUMNS,
          q: mhQ,
          getRowId: (r: Record<string, unknown>) =>
            r.misaMapId != null ? Number(r.misaMapId) : `${r.erpCp}|${r.erpInvNo}|${r.refid}`,
          selectable: true,
          search: 'CP, invoice, contract…',
        }
      case 'banhang':
        return {
          columns: BANHANG_COLUMNS,
          q: bhQ,
          getRowId: (r: Record<string, unknown>) =>
            r.misaMapId != null ? Number(r.misaMapId) : `${r.contractNo}|${r.erpInv}|${r.refId}`,
          selectable: true,
          search: 'Contract, invoice, customer…',
        }
      default:
        return {
          columns: inputColumns,
          q: inputQ,
          // Others rows have qualityCode = '' (empty, NOT nullish) → `??` would
          // return '' for every row, making them indistinguishable. Use `||` and
          // fall back to input_type (unique per Others row) so each row selects
          // individually (Access edits Others per-row by input_type).
          getRowId: (r: Record<string, unknown>) => String(r.qualityCode || r.inputType || r.inputName || ''),
          selectable: true,
          search: 'Quality, type…',
        }
    }
  }, [pageKey, bankQ, cpQ, svcQ, mhQ, bhQ, inputQ, inputColumns])

  // Per-page column menu (order/visibility) persisted via preference registry
  // (baseline `report_cashflow_columns.py`, sub-key = page key).
  const columnsCtrl = usePreferenceColumns({
    scope: 'report_cashflow',
    prefKey: 'columns',
    subKey: pageKey,
    allColumns: active.columns,
    widthsTableKey: 'report.cashflow',
  })

  const rows = (active.q.data?.items ?? []) as unknown as Record<string, unknown>[]
  const selectedRow = useMemo(
    () => (active.selectable ? rows.find((r) => active.getRowId(r) === selectedId) ?? null : null),
    [rows, active, selectedId],
  )

  const requireRow = (): boolean => {
    if (!selectedRow) {
      window.alert('Please choose a record!')
      return false
    }
    return true
  }

  const handleAction = (action: CashflowAction, rowRequired: boolean) => {
    if (!canEdit && action !== 'cp-need' && action !== 'cp-dup' && action !== 'cp-misa' && action !== 'svc-need' && action !== 'svc-dup' && action !== 'svc-misa' && action !== 'mh-need' && action !== 'mh-misa' && action !== 'bh-need' && action !== 'bh-misa') {
      return
    }
    if (rowRequired && !requireRow()) return
    switch (action) {
      case 'input':
        setInputOpen(true)
        break
      case 'cp-edit':
        setCpEditOpen(true)
        break
      case 'svc-edit':
        setSvcEditOpen(true)
        break
      case 'mh-new':
        setMhDialog({ mode: 'new' })
        break
      case 'mh-edit':
        setMhDialog({ mode: 'edit' })
        break
      case 'bh-new':
        setBhDialog({ mode: 'new' })
        break
      case 'bh-edit':
        setBhDialog({ mode: 'edit' })
        break
      case 'mh-delete': {
        const id = selectedRow?.misaMapId
        if (id == null) {
          window.alert('Please choose a record!')
          return
        }
        setConfirmDelete({ kind: 'mh', id: Number(id) })
        break
      }
      case 'bh-delete': {
        const id = selectedRow?.misaMapId
        if (id == null) {
          window.alert('Please choose a record!')
          return
        }
        setConfirmDelete({ kind: 'bh', id: Number(id) })
        break
      }
      default:
        // read-only viewer dialogs (need / dup / misa)
        setViewer(action)
        break
    }
  }

  const onConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      const res =
        confirmDelete.kind === 'mh'
          ? await deleteMuaHang(confirmDelete.id).unwrap()
          : await deleteBanHang(confirmDelete.id).unwrap()
      snack.success(res.message || 'Successfully Deleted')
      setSelectedId(null)
    } catch {
      snack.error('Could not delete the mapping.')
    } finally {
      setConfirmDelete(null)
    }
  }

  const renderButton = (b: ToolbarButtonCfg) => {
    const needsEdit = b.action.endsWith('-edit') || b.action.endsWith('-new') || b.action.endsWith('-delete') || b.action === 'input'
    const disabled = (needsEdit && !canEdit) || (b.rowRequired && (!selectedRow || !canEdit))
    return (
      <Button
        key={b.action}
        variant="outlined"
        size="small"
        color={b.color}
        sx={toolbarBtnSx}
        disabled={disabled}
        onClick={() => handleAction(b.action, b.rowRequired)}
        startIcon={ICONS[b.icon]}
        title={b.title ?? b.label}
      >
        <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
          {b.label}
        </Box>
      </Button>
    )
  }

  // Others page: each row is its OWN input_type (deduct_frm_tlg / fwd_usd_pu / …),
  // so the save key must be the SELECTED ROW's input_type — NOT '' (which would
  // collapse every Others row onto one bogus key). Input pages = page type.
  const isOthers = pageKey === 'others'
  const inputType = isOthers ? ((selectedRow?.inputType as string | undefined) ?? '') : pageKey

  // "Go to Page" — compact label + flat Autocomplete in the FilterBar (mirror
  // the ComparisonTab "Compare with" selector). Access combo is a flat Value
  // List (no groups) → keep flat, exact captions.
  const pageSelector = (
    <Autocomplete
      size="small"
      disableClearable
      options={CASHFLOW_PAGES}
      getOptionLabel={(o) => o.label}
      value={page}
      isOptionEqualToValue={(o, v) => o.key === v.key}
      onChange={(_, v) => {
        if (!v) return
        setPageKey(v.key)
        setSelectedId(null)
      }}
      slotProps={{
        paper: { sx: { minWidth: 280, '& .MuiAutocomplete-option': { fontSize: '0.72rem', minHeight: 'auto', py: 0.5 } } },
      }}
      sx={{
        width: { xs: 200, sm: 290 },
        '& .MuiOutlinedInput-root': { py: 0.25 },
        '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '0.72rem' } },
        '& .MuiInputLabel-root': { fontSize: '0.72rem' },
        '& .MuiAutocomplete-popupIndicator': { p: 0.25 },
      }}
      renderInput={(params) => <TextField {...params} label="Go to Page" InputLabelProps={{ shrink: true }} />}
    />
  )

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CashflowDataList
        leftSlot={pageSelector}
        columns={columnsCtrl.visibleColumns}
        rows={rows}
        getRowId={active.getRowId}
        loading={active.q.isLoading}
        fetching={active.q.isFetching}
        error={!!active.q.error}
        errorTitle="Could not load Cash Flow data"
        emptyTitle="No records found"
        searchPlaceholder={active.search}
        selectable={active.selectable}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onOpenColumns={columnsCtrl.openMenu}
        toolbar={page.buttons.map(renderButton)}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />

      {/* ── Edit dialogs ───────────────────────────────────────────────── */}
      <InputValueDialog
        open={inputOpen}
        inputType={inputType}
        isOthers={isOthers}
        inputName={isOthers ? ((selectedRow?.inputName as string | undefined) ?? null) : null}
        pageLabel={page.label}
        weeks={weeks ?? []}
        selectedQuality={isInputPage(pageKey) ? (selectedRow?.qualityCode as string | undefined) ?? null : null}
        seedRow={isInputPage(pageKey) ? selectedRow : null}
        canEdit={canEdit}
        onClose={() => setInputOpen(false)}
      />
      <CpMappingEditDialog
        open={cpEditOpen}
        row={pageKey === 'cp' ? selectedRow : null}
        canEdit={canEdit}
        onClose={() => setCpEditOpen(false)}
        onSaved={() => setSelectedId(null)}
      />
      <ServiceMappingEditDialog
        open={svcEditOpen}
        row={pageKey === 'service' ? selectedRow : null}
        canEdit={canEdit}
        onClose={() => setSvcEditOpen(false)}
        onSaved={() => setSelectedId(null)}
      />
      <MuaHangMapDialog
        open={mhDialog !== null}
        mode={mhDialog?.mode ?? 'new'}
        row={mhDialog?.mode === 'edit' ? (selectedRow as unknown as MapMuaHang | null) : null}
        canEdit={canEdit}
        onClose={() => setMhDialog(null)}
        onSaved={() => setSelectedId(null)}
      />
      <BanHangMapDialog
        open={bhDialog !== null}
        mode={bhDialog?.mode ?? 'new'}
        row={bhDialog?.mode === 'edit' ? (selectedRow as unknown as MapBanHang | null) : null}
        canEdit={canEdit}
        onClose={() => setBhDialog(null)}
        onSaved={() => setSelectedId(null)}
      />

      {/* ── Read-only viewer dialogs (need / dup / misa) ───────────────── */}
      <CashflowViewerDialog action={viewer} onClose={() => setViewer(null)} />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Mapping"
        message="Do you want to delete this records?"
        confirmLabel="Delete"
        danger
        onCancel={() => setConfirmDelete(null)}
        onConfirm={onConfirmDelete}
      />
    </Box>
  )
}
